// Per-location state for the Snapshot, read from the same transforms the
// existing views use so the figures reconcile by construction:
//
// - Hub-and-spoke: `supply_walk_cora` / `inventory_wos_cora_matrix`
//   segmented ["location", "location_role"] — the exact source /inventory-wos
//   renders (hub + spoke + hub_spoke + in_transit union). A second call
//   segmented ["sku", "location_role"], filtered to the stock-out SKUs,
//   fills the "Hub can cover" column. Rebalance units come from
//   `transfer_recommendations_matrix_hub_to_spoke` segmented ["location"].
// - Flat: `supply_walk_test` / `supply_walk_parallelized_matrix` segmented
//   ["location"] — units, WOS, target and in-stock rate per DC.
//
// Figures neither source carries (per-location $ value, weekly outflow,
// vendor-inbound units, ETAs) stay null and render as em dashes.
import { useEffect, useMemo, useRef, useState } from "react";
import * as arrow from "apache-arrow";
import { usePerspective, useWasm } from "../../contexts/app/provider";
import {
  GetTransformedParquetDownloadUrlDocument,
  GetTransformedRawParquetDocument,
} from "../../services/perspective_api/__generated__/perspective_server-types";
import {
  isChunkedParsedData,
  parseParquetData,
  parseParquetDataWithRefresh,
} from "../../utils/parquet";
import dayjs from "../../utils/dayjs";
import { Run } from "../../services/appsync/types";
import { Alerts } from "./comparison_types";
import {
  SnapshotChannel,
  SnapshotChannelMixEntry,
  SnapshotInbound,
  SnapshotLocation,
  SnapshotPeriod,
  SnapshotRebalanceCard,
  SnapshotSales,
} from "./types";
import { OTHER_CHANNEL_LABEL } from "./primitives";

export type SnapshotLocationsResult = {
  locations: SnapshotLocation[];
  inbound: SnapshotInbound;
  sales: SnapshotSales;
  rebalance: SnapshotRebalanceCard[];
  /** Total units on trucks between hub and spokes (supply_walk_cora ledger). */
  inTransitUnits: number | null;
  /** Open hub→spoke transfers over the near horizon, per lane; null while
   *  the transfer matrix hasn't loaded. */
  transferLanes:
    | {
        fromHubId: string;
        toLocationId: string;
        units: number;
        eta: string | null;
      }[]
    | null;
  /** Per-SKU units available at the hub, for the "Hub can cover" column. */
  hubStockBySku: Map<string, number> | null;
  /** Actual shipments over the 13 full weeks before the run's data date. */
  trailingActuals: { units: number | null; revenue: number | null };
  /** Demand-weighted in-stock rate across SKU × location (0..100). */
  networkInStock: number | null;
  /** Total cost of placed-not-delivered POs. */
  openPoValue: number | null;
  /** Month-forward forecast by channel, keyed "sku|location". */
  channelForecast: Map<string, ChannelForecastRow[]> | null;
  /** Top channels for the selected period, remainder rolled up. */
  channels: SnapshotChannel[];
  /** Locations omitted for having no stock and no forecast demand. Surfaced
   *  rather than silently dropped, so a shrinking network is legible. */
  emptyLocationsHidden: number;
  /** The month the month-mode figures cover, e.g. "August 2026". */
  monthLabel: string;
  /** Complete weeks of that month included so far. 0 means the month has no
   *  complete week yet, and every month figure is absent rather than partial. */
  monthWeeks: number;
  /** Risk rows computed from SKU-level walk state — the run's CURRENT values
   *  at the as-of week. Preferred over the comparison-summary alerts, whose
   *  quantities were measured (staging, 2026-08-20) to contradict the run's
   *  own walk. */
  computedRisks: {
    stockOuts: {
      sku: string;
      skuName: string | null;
      location: string;
      recentDemand: number;
      wos: number | null;
      target: number | null;
    }[];
    lowStock: {
      sku: string;
      skuName: string | null;
      location: string;
      quantity: number;
      threshold: number | null;
      daysUntilStockout: number;
      wos: number | null;
      target: number | null;
    }[];
    excess: {
      sku: string;
      skuName: string | null;
      location: string;
      quantity: number;
      value: number | null;
      monthsOfSupply: number;
      monthlyDemand: number;
      wos: number | null;
      target: number | null;
    }[];
  };
  /** True once the risk walk tables have produced SKU rows. Distinguishes
   *  "walk loaded, zero risks" (trust the empty lists) from "walk not loaded"
   *  (fall back to the alerts rather than showing an empty table). */
  computedRisksReady: boolean;
  loading: boolean;
};

/**
 * Subscribes to getTransformedRawParquet for one transform call and returns
 * the parsed arrow table. Follows the ad-hoc pattern from
 * routes/supply_walk_editable/nested_details.tsx. `variables: null` skips.
 */
const useMatrixTable = (
  variables: Record<string, unknown> | null
): { table: arrow.Table | null; loading: boolean } => {
  const { perspectiveApiService } = usePerspective();
  const { wasmInitialized } = useWasm();
  const [result, setResult] = useState<{
    key: string;
    table: arrow.Table | null;
    loading: boolean;
  }>({ key: "", table: null, loading: false });
  const variablesRef = useRef(variables);
  variablesRef.current = variables;
  const key = variables ? JSON.stringify(variables) : "";

  useEffect(() => {
    const vars = variablesRef.current;
    if (!vars || !wasmInitialized) {
      setResult({ key, table: null, loading: !!vars });
      return;
    }
    let cancelled = false;
    setResult({ key, table: null, loading: true });
    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({ query: GetTransformedRawParquetDocument, variables: vars })
      .subscribe({
        next: async (res: any) => {
          const payload = res?.data?.getTransformedRawParquet;
          if (payload?.status === "FAILED") {
            if (!cancelled) {
              setResult({ key, table: null, loading: false });
            }
            return;
          }
          const parquetData = payload?.parquetData;
          if (!parquetData || typeof parquetData !== "string") {
            return;
          }
          const isBase64 = !parquetData.startsWith("http");
          try {
            const parsed = isBase64
              ? await parseParquetData(parquetData, true)
              : await parseParquetDataWithRefresh(parquetData, async () => {
                  const refreshVariables = { ...vars };
                  delete refreshVariables.format;
                  const refreshed = await client.query({
                    query: GetTransformedParquetDownloadUrlDocument,
                    variables: refreshVariables,
                  });
                  return (
                    refreshed.data?.getTransformedParquetDownloadUrl
                      ?.downloadUrl ?? null
                  );
                });
            if (!cancelled) {
              setResult({
                key,
                table:
                  parsed && !isChunkedParsedData(parsed)
                    ? parsed.arrowTable
                    : null,
                loading: false,
              });
            }
          } catch {
            if (!cancelled) {
              setResult({ key, table: null, loading: false });
            }
          }
        },
        error: () => {
          if (!cancelled) {
            setResult({ key, table: null, loading: false });
          }
        },
      });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [key, wasmInitialized, perspectiveApiService]);

  return result.key === key
    ? { table: result.table, loading: result.loading }
    : { table: null, loading: !!variables };
};

/** Period columns are epoch-ms-as-string; pick the latest one not after
 *  the run's as-of instant. */
const pickAsOfColumn = (table: arrow.Table, asOfMs: number): string | null => {
  const periodCols = table.schema.fields
    .map((f) => f.name)
    .filter((n) => /^\d+$/.test(n))
    .sort((a, b) => Number(a) - Number(b));
  if (!periodCols.length) {
    return null;
  }
  const past = periodCols.filter((n) => Number(n) <= asOfMs);
  return past.length ? past[past.length - 1] : periodCols[0];
};

type MatrixRow = {
  segs: Record<string, string>;
  measure: string;
  value: number | null;
};

const toNumber = (v: unknown): number | null => {
  if (v == null) {
    return null;
  }
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

/** One value per row, read at the as-of period column. */
const extractAsOfRows = (
  table: arrow.Table | null,
  segFields: string[],
  asOfMs: number
): MatrixRow[] => {
  if (!table) {
    return [];
  }
  const asOf = pickAsOfColumn(table, asOfMs);
  if (!asOf) {
    return [];
  }
  const measureVec = table.getChild("measure");
  const valueVec = table.getChild(asOf);
  const segVecs = segFields.map((f) => table.getChild(f));
  const rows: MatrixRow[] = [];
  for (let i = 0; i < table.numRows; i++) {
    rows.push({
      segs: Object.fromEntries(
        segFields.map((f, j) => [f, String(segVecs[j]?.get(i) ?? "")])
      ),
      measure: String(measureVec?.get(i) ?? "").toLowerCase(),
      value: toNumber(valueVec?.get(i)),
    });
  }
  return rows;
};

/** Sum of a measure across every period column, per segmentation tuple.
 *  Keys are the segmentation values joined with "|"; `weeks` is the number
 *  of period columns, for turning a horizon sum into a weekly rate. */
const extractHorizonSums = (
  table: arrow.Table | null,
  segFields: string[],
  measureName: string,
  range?: { fromMs: number; toMs: number }
): { sums: Map<string, number>; weeks: number } => {
  const sums = new Map<string, number>();
  if (!table) {
    return { sums, weeks: 0 };
  }
  // The fetched window is wider than the figure's definition (it is
  // quantized for cache reuse), so restrict to the intended periods.
  const periodCols = table.schema.fields
    .map((f) => f.name)
    .filter((n) => /^\d+$/.test(n))
    .filter(
      (n) =>
        !range || (Number(n) >= range.fromMs && Number(n) <= range.toMs)
    );
  const measureVec = table.getChild("measure");
  const segVecs = segFields.map((f) => table.getChild(f));
  const valueVecs = periodCols.map((c) => table.getChild(c));
  for (let i = 0; i < table.numRows; i++) {
    if (String(measureVec?.get(i) ?? "").toLowerCase() !== measureName) {
      continue;
    }
    const seg = segVecs.map((v) => String(v?.get(i) ?? "")).join("|");
    let total = sums.get(seg) ?? 0;
    for (const vec of valueVecs) {
      total += toNumber(vec?.get(i)) ?? 0;
    }
    sums.set(seg, total);
  }
  return { sums, weeks: periodCols.length };
};

export type ChannelForecastRow = { channel: string; units: number };

/** Long pivot rows summed per "sku|location", split by channel, descending. */
const extractChannelForecast = (
  table: arrow.Table | null,
  range: { fromMs: number; toMs: number }
): Map<string, ChannelForecastRow[]> | null => {
  if (!table) {
    return null;
  }
  const dateVec = table.getChild("order_placed_week");
  const skuVec = table.getChild("sku");
  const locVec = table.getChild("location");
  const channelVec = table.getChild("channel");
  const qtyVec = table.getChild("quantity");
  if (!skuVec || !channelVec) {
    return null;
  }
  const byKey = new Map<string, Map<string, number>>();
  for (let i = 0; i < table.numRows; i++) {
    const units = toNumber(qtyVec?.get(i));
    const periodMs = toNumber(dateVec?.get(i));
    if (units == null || units <= 0) {
      continue;
    }
    // Only the weeks after the run's week — the fetched window is wider.
    if (periodMs == null || periodMs <= range.fromMs || periodMs > range.toMs) {
      continue;
    }
    const key =
      String(skuVec.get(i) ?? "") + "|" + String(locVec?.get(i) ?? "");
    const channel = String(channelVec.get(i) ?? "");
    const channels = byKey.get(key) ?? new Map<string, number>();
    channels.set(channel, (channels.get(channel) ?? 0) + units);
    byKey.set(key, channels);
  }
  const out = new Map<string, ChannelForecastRow[]>();
  for (const [key, channels] of byKey) {
    out.set(
      key,
      [...channels.entries()]
        .map(([channel, units]) => ({ channel, units: Math.round(units) }))
        .sort((a, b) => b.units - a.units)
    );
  }
  return out;
};

type FlowRow = {
  periodMs: number;
  location: string;
  channel: string;
  value: number;
};

/** Long pivot rows at (period × location × channel) grain. */
const extractFlowRows = (
  table: arrow.Table | null,
  dateField: string,
  valueField: string
): FlowRow[] => {
  if (!table) {
    return [];
  }
  const dateVec = table.getChild(dateField);
  const locVec = table.getChild("location");
  const channelVec = table.getChild("channel");
  const valueVec = table.getChild(valueField);
  const rows: FlowRow[] = [];
  for (let i = 0; i < table.numRows; i++) {
    const periodMs = toNumber(dateVec?.get(i));
    const value = toNumber(valueVec?.get(i));
    if (periodMs == null || value == null) {
      continue;
    }
    rows.push({
      periodMs,
      location: String(locVec?.get(i) ?? ""),
      channel: normalizeChannel(String(channelVec?.get(i) ?? "")),
      value,
    });
  }
  return rows;
};

/** Long pivot rows at (period × channel) grain. */
const extractChannelValueRows = (
  table: arrow.Table | null,
  dateField: string,
  valueField: string
): { periodMs: number; channel: string; value: number }[] => {
  if (!table) {
    return [];
  }
  const dateVec = table.getChild(dateField);
  const channelVec = table.getChild("channel");
  const valueVec = table.getChild(valueField);
  const rows: { periodMs: number; channel: string; value: number }[] = [];
  for (let i = 0; i < table.numRows; i++) {
    const periodMs = toNumber(dateVec?.get(i));
    const value = toNumber(valueVec?.get(i));
    if (periodMs == null || value == null) {
      continue;
    }
    rows.push({
      periodMs,
      channel: normalizeChannel(String(channelVec?.get(i) ?? "")),
      value,
    });
  }
  return rows;
};

type WeeklyRow = { weekMs: number; seg: string; value: number };

/** Long pivot rows: one value per (date, group) — sales_order_master shape. */
const extractWeeklyRows = (
  table: arrow.Table | null,
  dateField: string,
  segField: string,
  valueField: string
): WeeklyRow[] => {
  if (!table) {
    return [];
  }
  const dateVec = table.getChild(dateField);
  const segVec = table.getChild(segField);
  const valueVec = table.getChild(valueField);
  const rows: WeeklyRow[] = [];
  for (let i = 0; i < table.numRows; i++) {
    const weekMs = toNumber(dateVec?.get(i));
    const value = toNumber(valueVec?.get(i));
    if (weekMs == null || value == null) {
      continue;
    }
    rows.push({ weekMs, seg: String(segVec?.get(i) ?? ""), value });
  }
  return rows;
};

const IN_TRANSIT_LOCATION = "In Transit";
const RECENT_WEEKS = 4;
const TRAILING_WEEKS = 13;
const WEEKS_PER_MONTH = 52 / 12;
/** Named channels shown individually; the rest roll into one remainder. */
const TOP_CHANNELS = 5;
/** Values that mean "no channel recorded" rather than naming a channel.
 *  Some customers carry a single placeholder value for every row, which is
 *  not a split worth drawing. */
const PLACEHOLDER_CHANNELS = new Set([
  "",
  "n/a",
  "na",
  "none",
  "null",
  "__null__",
  "unknown",
  "unspecified",
]);
const UNSPECIFIED_CHANNEL = "Unspecified";
const normalizeChannel = (raw: string): string =>
  PLACEHOLDER_CHANNELS.has(raw.trim().toLowerCase())
    ? UNSPECIFIED_CHANNEL
    : raw;

export const useSnapshotLocations = ({
  run,
  isHubAndSpoke,
  alerts,
  period,
  dataAsOfMs,
}: {
  run: Run | null;
  isHubAndSpoke: boolean;
  alerts: Alerts | null;
  period: SnapshotPeriod;
  /** The engine's data boundary (today_proxy). Falls back to the run's own
   *  timestamp, which runs days later and would count forecast as actual. */
  dataAsOfMs: number | null;
}): SnapshotLocationsResult => {
  const runSettingsJson = run?.runSettings ?? null;
  const isMonth = period === "month";

  // Server-side windows are QUANTIZED TO THE RUN'S MONTH, while the exact
  // periods are selected client-side from `bounds` below.
  //
  // Why: the transform cache is keyed on a hash of these params, so a window
  // derived from the run's own date gives every run a unique key and nothing
  // is ever reusable — including the previous run a delta would need.
  // Quantizing to the month means consecutive runs send byte-identical
  // params and share cache entries, while the window still contains the
  // run's as-of date, so opening an older run stays correct.
  const { minDate, maxDate, horizonMaxDate, asOfMs } = useMemo(() => {
    const asOf = dataAsOfMs
      ? dayjs.utc(dataAsOfMs)
      : run
      ? dayjs.utc(run.createdAt)
      : dayjs.utc();
    const anchor = asOf.startOf("month");
    return {
      minDate: anchor.subtract(1, "month").toDate(),
      maxDate: anchor.add(2, "month").toDate(),
      horizonMaxDate: anchor.add(4, "month").toDate(),
      asOfMs: asOf.valueOf(),
    };
  }, [run, dataAsOfMs]);

  /** Exact period boundaries for client-side slicing. The server window is
   *  deliberately wider and coarser; these keep every figure's definition
   *  identical to a run-derived fetch. */
  const bounds = useMemo(() => {
    const asOf = dataAsOfMs
      ? dayjs.utc(dataAsOfMs)
      : run
      ? dayjs.utc(run.createdAt)
      : dayjs.utc();
    const daysSinceMonday = (asOf.day() + 6) % 7;
    const weekStart = asOf.subtract(daysSinceMonday, "day").startOf("day");
    return {
      weekStartMs: weekStart.valueOf(),
      trailingStartMs: weekStart.subtract(TRAILING_WEEKS, "week").valueOf(),
      recentCutoffMs: weekStart.subtract(RECENT_WEEKS, "week").valueOf(),
      // Month to date, as a half-open range over WEEK buckets: every complete
      // week whose Monday falls in the as-of month. The end bound is the start
      // of the in-progress week, which sales_order_master fills with forecast.
      mtdStartMs: asOf.startOf("month").valueOf(),
      mtdEndMs: weekStart.valueOf(),
      forecastStartMs: weekStart.valueOf(),
      forecastEndMs: weekStart.add(4, "week").valueOf(),
      // Reproduces the previous walk window (weekStart-1w .. +3w).
      walkStartMs: weekStart.subtract(1, "week").valueOf(),
      walkEndMs: weekStart.add(3, "week").valueOf(),
      // Reproduces the previous rebalance horizon (weekStart-1w .. +9w).
      rebalanceEndMs: weekStart.add(9, "week").valueOf(),
      // Reproduces the previous open-PO window.
      poStartMs: weekStart.subtract(13, "week").valueOf(),
      poEndMs: weekStart.add(52, "week").valueOf(),
    };
  }, [run, dataAsOfMs]);

  // The four full weeks AFTER the run's week — sales_order_master's future
  // rows are the forecast, so this is "the month forward".
  const { forecastMinDate, forecastMaxDate } = useMemo(() => {
    const anchor = (dataAsOfMs
      ? dayjs.utc(dataAsOfMs)
      : run
      ? dayjs.utc(run.createdAt)
      : dayjs.utc()
    ).startOf("month");
    return {
      forecastMinDate: anchor.toDate(),
      forecastMaxDate: anchor.add(3, "month").toDate(),
    };
  }, [run, dataAsOfMs]);

  // Month-mode flow: the as-of month TO DATE, summed from the run's own
  // weekly rows — never a weekly figure scaled by 4.35.
  //
  // Why weekly rows and not the monthly ones: sales_order_master concatenates
  // history and simulation with no column marking which is which (see
  // logic_sales_order_master/generic.py), so the only actual/forecast boundary
  // is the date. A month bucket for the as-of month is one summed row spanning
  // the whole month, and would silently carry the rest of the month's forecast
  // into a figure labelled "to date". Week buckets are fine enough to cut at
  // the data boundary, so month-to-date is the complete weeks of this month.
  const { monthLabel, monthWeeks } = useMemo(() => {
    const asOf = dataAsOfMs
      ? dayjs.utc(dataAsOfMs)
      : run
      ? dayjs.utc(run.createdAt)
      : dayjs.utc();
    const monthStart = asOf.startOf("month");
    const daysSinceMonday = (asOf.day() + 6) % 7;
    const weekStart = asOf.subtract(daysSinceMonday, "day").startOf("day");
    // Mondays in [monthStart, weekStart) — the complete weeks this figure
    // covers. The offset is 0 when the month itself starts on a Monday.
    let weeks = 0;
    let cursor = monthStart.add((8 - monthStart.day()) % 7, "day");
    while (cursor.valueOf() < weekStart.valueOf()) {
      weeks += 1;
      cursor = cursor.add(1, "week");
    }
    return {
      monthLabel: monthStart.format("MMMM YYYY"),
      monthWeeks: weeks,
    };
  }, [run, dataAsOfMs]);

  // Sales actuals window: the 13 full Monday-start weeks strictly before
  // the week containing the run's data date. sales_order_master carries
  // forecast rows for future weeks, and the in-progress week is
  // forecast-filled — both must stay out of "actuals".
  const { salesMinDate, salesMaxDate } = useMemo(() => {
    const anchor = (dataAsOfMs
      ? dayjs.utc(dataAsOfMs)
      : run
      ? dayjs.utc(run.createdAt)
      : dayjs.utc()
    ).startOf("month");
    return {
      salesMinDate: anchor.subtract(5, "month").toDate(),
      salesMaxDate: anchor.add(1, "month").toDate(),
    };
  }, [run, dataAsOfMs]);
  const recentCutoffMs = bounds.recentCutoffMs;

  const baseParams = useMemo(
    () => ({
      min_date: minDate,
      max_date: maxDate,
      date_field: "week",
      filters: [] as [string, string, string[]][],
      post_filters: [] as unknown[],
    }),
    [minDate, maxDate]
  );

  // ── Call 1: per-location inventory / WOS / target ─────────────
  const locationVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: isHubAndSpoke ? "supply_walk_cora" : "supply_walk_test",
      transformId: isHubAndSpoke
        ? "inventory_wos_cora_matrix"
        : "supply_walk_parallelized_matrix",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        ...baseParams,
        measure: isHubAndSpoke ? "Inventory" : "Ending Inventory On Hand",
        value_field: isHubAndSpoke ? "Inventory" : "Ending Inventory On Hand",
        segmentation_fields: isHubAndSpoke
          ? ["location", "location_role"]
          : ["location"],
      },
    };
  }, [run, isHubAndSpoke, runSettingsJson, baseParams]);
  const { table: locationTable, loading: locationsLoading } =
    useMatrixTable(locationVariables);

  // ── Call 3 (hub only): recommended hub→spoke movement ─────────
  const rebalanceVariables = useMemo(() => {
    if (!run || !isHubAndSpoke) {
      return null;
    }
    return {
      datasetName: "supply_walk_test_spoke",
      transformId: "transfer_recommendations_matrix_hub_to_spoke",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        ...baseParams,
        max_date: horizonMaxDate,
        measure: "Transfer Order To Place",
        value_field: "Transfer Order To Place",
        // source_hub must be requested explicitly — the transform's forced
        // segmentation does not survive into the wide matrix's columns.
        segmentation_fields: ["location", "source_hub"],
      },
    };
  }, [run, isHubAndSpoke, runSettingsJson, baseParams, horizonMaxDate]);
  const { table: rebalanceTable, loading: rebalanceLoading } =
    useMatrixTable(rebalanceVariables);

  // ── Calls 4 + 5: actual shipments, weekly, at location × channel grain.
  // One fetch serves three needs: trailing 13-week totals, week-mode flow
  // (its last 4 complete weeks), and the per-location channel mix. Reading
  // location outflow and channel totals from the SAME rows is what makes
  // them reconcile on screen instead of disagreeing by a rounding.
  const salesQtyVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: "sales_order_master",
      transformId: "sales_order_master_date_pivot",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        min_date: salesMinDate,
        max_date: salesMaxDate,
        date_field: "order_placed_week",
        value_field: "quantity",
        group_by_fields: ["location", "channel"],
        filters: [] as unknown[],
      },
    };
  }, [run, runSettingsJson, salesMinDate, salesMaxDate]);
  const { table: salesQtyTable, loading: salesQtyLoading } =
    useMatrixTable(salesQtyVariables);

  const salesRevVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: "sales_order_master",
      transformId: "sales_order_master_date_pivot",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        min_date: salesMinDate,
        max_date: salesMaxDate,
        date_field: "order_placed_week",
        value_field: "revenue",
        group_by_fields: ["channel"],
        filters: [] as unknown[],
      },
    };
  }, [run, runSettingsJson, salesMinDate, salesMaxDate]);
  const { table: salesRevTable, loading: salesRevLoading } =
    useMatrixTable(salesRevVariables);

  // Month mode reads its flow from the weekly fetches above (see the
  // month-to-date note), so it needs no monthly sales fetch of its own.

  // Month-mode supply figures (months of supply / target). Health color
  // still comes from the weekly matrix — see the ratio note below.
  const monthSupplyVariables = useMemo(() => {
    if (!run || !isMonth) {
      return null;
    }
    return {
      datasetName: isHubAndSpoke ? "supply_walk_cora" : "supply_walk_test",
      transformId: isHubAndSpoke
        ? "inventory_wos_cora_matrix"
        : "supply_walk_parallelized_matrix",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        min_date: minDate,
        max_date: maxDate,
        date_field: "month",
        measure: isHubAndSpoke ? "Inventory" : "Ending Inventory On Hand",
        value_field: isHubAndSpoke ? "Inventory" : "Ending Inventory On Hand",
        segmentation_fields: isHubAndSpoke
          ? ["location", "location_role"]
          : ["location"],
        filters: [] as [string, string, string[]][],
        post_filters: [] as unknown[],
      },
    };
  }, [run, isMonth, isHubAndSpoke, runSettingsJson, minDate, maxDate]);
  const { table: monthSupplyTable, loading: monthSupplyLoading } =
    useMatrixTable(monthSupplyVariables);

  // ── Call 6: inventory value ($ cost) by location, as of the run ──
  const invValueVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: "inventory_forecast_master",
      transformId: "inventory_forecast_master_date_pivot",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        min_date: minDate,
        max_date: maxDate,
        date_field: "week",
        value_field: "cost",
        group_by_fields: ["location"],
        filters: [] as unknown[],
      },
    };
  }, [run, runSettingsJson, minDate, maxDate]);
  const { table: invValueTable, loading: invValueLoading } =
    useMatrixTable(invValueVariables);

  // Same dataset at SKU × location grain: $ value for the risk lists.
  const invSkuValueVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: "inventory_forecast_master",
      transformId: "inventory_forecast_master_date_pivot",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        min_date: minDate,
        max_date: maxDate,
        date_field: "week",
        value_field: "cost",
        group_by_fields: ["sku", "location"],
        filters: [] as unknown[],
      },
    };
  }, [run, runSettingsJson, minDate, maxDate]);
  const { table: invSkuValueTable, loading: invSkuValueLoading } =
    useMatrixTable(invSkuValueVariables);

  // ── Calls 7 + 8: open POs (placed, not delivered) by ETA week ──
  // Includes past-due ETAs: a late PO is still open and still inbound.
  const poWindow = useMemo(
    () => ({
      min_date: dayjs.utc(minDate).subtract(4, "month").toDate(),
      max_date: dayjs.utc(minDate).add(13, "month").toDate(),
    }),
    [minDate]
  );
  const poQtyVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: "purchase_order_master",
      transformId: "purchase_order_master_date_pivot",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        ...poWindow,
        date_field: "eta_to_warehouse_week",
        value_field: "quantity",
        group_by_fields: ["location"],
        filters: [["status", "in", ["Ordered"]]] as unknown[],
      },
    };
  }, [run, runSettingsJson, poWindow]);
  const { table: poQtyTable, loading: poQtyLoading } =
    useMatrixTable(poQtyVariables);

  const poCostVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: "purchase_order_master",
      transformId: "purchase_order_master_date_pivot",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        ...poWindow,
        date_field: "eta_to_warehouse_week",
        value_field: "cost",
        group_by_fields: [] as string[],
        filters: [["status", "in", ["Ordered"]]] as unknown[],
      },
    };
  }, [run, runSettingsJson, poWindow]);
  const { table: poCostTable, loading: poCostLoading } =
    useMatrixTable(poCostVariables);

  // ── Calls 9 (+10 for hubs): SKU × location state for risk lists ──
  // Flat networks read the health matrix; hub customers need both the hub
  // walk and the spoke walk to cover every location.
  const riskParams = useMemo(
    () => ({
      ...baseParams,
      measure: "Ending Inventory On Hand",
      value_field: "Ending Inventory On Hand",
      // sku_name rides along to label the risk rows. It is 1:1 with sku, so
      // the grain is unchanged; verified present in every active customer's
      // walk datasets (staging, 2026-08-20). A customer with no walk at all
      // never reaches this transform.
      segmentation_fields: ["sku", "sku_name", "location"],
    }),
    [baseParams]
  );
  const riskPrimaryVariables = useMemo(() => {
    if (!run) {
      return null;
    }
    return {
      datasetName: "supply_walk_test",
      transformId: isHubAndSpoke
        ? "supply_walk_parallelized_matrix_for_hubs"
        : "supply_walk_parallelized_matrix",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: riskParams,
    };
  }, [run, isHubAndSpoke, runSettingsJson, riskParams]);
  const { table: riskPrimaryTable, loading: riskPrimaryLoading } =
    useMatrixTable(riskPrimaryVariables);

  const riskSpokeVariables = useMemo(() => {
    if (!run || !isHubAndSpoke) {
      return null;
    }
    return {
      datasetName: "supply_walk_test_spoke",
      transformId: "transfer_recommendations_matrix_hub_to_spoke",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: riskParams,
    };
  }, [run, isHubAndSpoke, runSettingsJson, riskParams]);
  const { table: riskSpokeTable, loading: riskSpokeLoading } =
    useMatrixTable(riskSpokeVariables);

  // SKU × location state at the as-of week, keyed "sku|location". Demand is
  // the average weekly forecast over the whole fetched window — the as-of
  // week's own forecast cell is often null/zero because the week has
  // already started.
  const skuState = useMemo(() => {
    const map = new Map<
      string,
      {
        sku: string;
        skuName: string | null;
        location: string;
        onHand: number | null;
        wos: number | null;
        target: number | null;
        weeklyDemand: number | null;
        inStock: number | null;
      }
    >();
    // The transforms fill null segmentation values with the "__NULL__"
    // placeholder — a SKU with no name must read as nameless, not as that
    // literal string.
    const cleanName = (v: string): string | null =>
      v && v !== "__NULL__" ? v : null;
    for (const table of [riskPrimaryTable, riskSpokeTable]) {
      for (const row of extractAsOfRows(
        table,
        ["sku", "sku_name", "location"],
        asOfMs
      )) {
        const key = row.segs.sku + "|" + row.segs.location;
        const acc = map.get(key) ?? {
          sku: row.segs.sku,
          skuName: cleanName(row.segs.sku_name),
          location: row.segs.location,
          onHand: null,
          wos: null,
          target: null,
          weeklyDemand: null,
          inStock: null,
        };
        if (row.value != null) {
          if (row.measure === "ending inventory on hand") {
            acc.onHand = row.value;
          } else if (row.measure === "ending weeks of supply") {
            acc.wos = row.value;
          } else if (row.measure === "target weeks of supply") {
            acc.target = row.value;
          } else if (row.measure === "in stock rate") {
            acc.inStock = row.value;
          }
        }
        map.set(key, acc);
      }
      const { sums, weeks } = extractHorizonSums(
        table,
        ["sku", "location"],
        "forecasted sales",
        { fromMs: bounds.walkStartMs, toMs: bounds.walkEndMs }
      );
      if (weeks > 0) {
        for (const [key, total] of sums) {
          const acc = map.get(key);
          if (acc) {
            // The walk emits forecasted sales as NEGATIVE outflows (every
            // matrix path negates positive sales), so the magnitude is the
            // demand. Without it the stock-out rule (forecast > 0) can never
            // fire — verified against staging cleanboss/cora 2026-08-20.
            acc.weeklyDemand = Math.abs(total) / weeks;
          }
        }
      }
    }
    return map;
  }, [riskPrimaryTable, riskSpokeTable, asOfMs, bounds]);

  // $ value per "sku|location" at the as-of week, for the risk lists.
  const skuLocValue = useMemo(() => {
    if (!invSkuValueTable) {
      return null;
    }
    const dateVec = invSkuValueTable.getChild("week");
    const skuVec = invSkuValueTable.getChild("sku");
    const locVec = invSkuValueTable.getChild("location");
    const costVec = invSkuValueTable.getChild("cost");
    let asOfWeekMs: number | null = null;
    for (let i = 0; i < invSkuValueTable.numRows; i++) {
      const ms = toNumber(dateVec?.get(i));
      if (ms != null && ms <= asOfMs && (asOfWeekMs == null || ms > asOfWeekMs)) {
        asOfWeekMs = ms;
      }
    }
    if (asOfWeekMs == null) {
      return null;
    }
    const map = new Map<string, number>();
    for (let i = 0; i < invSkuValueTable.numRows; i++) {
      if (toNumber(dateVec?.get(i)) !== asOfWeekMs) {
        continue;
      }
      const value = toNumber(costVec?.get(i));
      if (value == null) {
        continue;
      }
      const key = String(skuVec?.get(i) ?? "") + "|" + String(locVec?.get(i) ?? "");
      map.set(key, (map.get(key) ?? 0) + value);
    }
    return map;
  }, [invSkuValueTable, asOfMs]);

  // Risk rules mirror the app's existing bands (supply_color_coding):
  // stock-out = nothing on hand with live demand; low stock = under 0.8×
  // target cover; excess = over 3× target cover.
  const computedRisks = useMemo(() => {
    const stockOuts: {
      sku: string;
      skuName: string | null;
      location: string;
      recentDemand: number;
      wos: number | null;
      target: number | null;
    }[] = [];
    const lowStock: {
      sku: string;
      skuName: string | null;
      location: string;
      quantity: number;
      threshold: number | null;
      daysUntilStockout: number;
      wos: number | null;
      target: number | null;
    }[] = [];
    const excess: {
      sku: string;
      skuName: string | null;
      location: string;
      quantity: number;
      value: number | null;
      monthsOfSupply: number;
      monthlyDemand: number;
      wos: number | null;
      target: number | null;
    }[] = [];
    const WEEKS_PER_MONTH = 52 / 12;
    for (const s of skuState.values()) {
      const onHand = s.onHand ?? 0;
      // The walk's own demand basis: WOS = on hand ÷ weekly basis, so the
      // basis is on hand ÷ WOS. At hubs this includes transfer obligations,
      // which plain forecast sales would miss.
      const impliedWeekly =
        onHand > 0 && s.wos != null && s.wos > 0 ? onHand / s.wos : null;
      const demand = impliedWeekly ?? s.weeklyDemand ?? 0;
      if (onHand <= 0) {
        const forecast = s.weeklyDemand ?? 0;
        if (forecast > 0) {
          stockOuts.push({
            sku: s.sku,
            skuName: s.skuName,
            location: s.location,
            recentDemand: Math.round(forecast),
            wos: s.wos,
            target: s.target,
          });
        }
        continue;
      }
      if (s.wos == null || s.target == null || s.target <= 0) {
        continue;
      }
      if (s.wos < 0.8 * s.target) {
        lowStock.push({
          sku: s.sku,
          skuName: s.skuName,
          location: s.location,
          quantity: onHand,
          threshold: demand > 0 ? Math.round(demand * s.target) : null,
          daysUntilStockout: Math.round(s.wos * 7),
          wos: s.wos,
          target: s.target,
        });
      } else if (s.wos > 3 * s.target) {
        excess.push({
          sku: s.sku,
          skuName: s.skuName,
          location: s.location,
          quantity: onHand,
          value: skuLocValue?.get(s.sku + "|" + s.location) ?? null,
          monthsOfSupply: s.wos / WEEKS_PER_MONTH,
          monthlyDemand: Math.round(demand * WEEKS_PER_MONTH),
          wos: s.wos,
          target: s.target,
        });
      }
    }
    return { stockOuts, lowStock, excess };
  }, [skuState, skuLocValue]);

  // Network in-stock rate from the same SKU-level rows: demand-weighted
  // when forecast demand exists, plain average otherwise.
  const networkInStock = useMemo(() => {
    let weighted = 0;
    let totalDemand = 0;
    let plain = 0;
    let count = 0;
    for (const s of skuState.values()) {
      if (s.inStock == null) {
        continue;
      }
      plain += s.inStock;
      count += 1;
      if (s.weeklyDemand != null && s.weeklyDemand > 0) {
        weighted += s.inStock * s.weeklyDemand;
        totalDemand += s.weeklyDemand;
      }
    }
    if (totalDemand > 0) {
      return (weighted / totalDemand) * 100;
    }
    return count > 0 ? (plain / count) * 100 : null;
  }, [skuState]);

  // ── Call 2 (hub only): hub availability for stocked-out SKUs ──
  const stockOutSkus = useMemo(() => {
    const fromAlerts = (alerts?.stock_outs.items ?? []).map((a) => a.sku);
    const fromComputed = computedRisks.stockOuts.map((s) => s.sku);
    return [...new Set([...fromAlerts, ...fromComputed])].sort();
  }, [alerts, computedRisks]);
  const hubCoverVariables = useMemo(() => {
    if (!run || !isHubAndSpoke || !stockOutSkus.length) {
      return null;
    }
    return {
      datasetName: "supply_walk_cora",
      transformId: "inventory_wos_cora_matrix",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        ...baseParams,
        measure: "Inventory",
        value_field: "Inventory",
        segmentation_fields: ["sku", "location_role"],
        filters: [["sku", "in", stockOutSkus]] as [string, string, string[]][],
      },
    };
  }, [run, isHubAndSpoke, stockOutSkus, runSettingsJson, baseParams]);
  const { table: hubCoverTable, loading: hubCoverLoading } =
    useMatrixTable(hubCoverVariables);

  // Month-forward forecast by channel, for the expandable low-stock rows.
  const lowStockSkus = useMemo(() => {
    const fromAlerts = (alerts?.low_stock.items ?? []).map((a) => a.sku);
    const fromComputed = computedRisks.lowStock.map((r) => r.sku);
    return [...new Set([...fromAlerts, ...fromComputed])].sort();
  }, [alerts, computedRisks]);
  const channelForecastVariables = useMemo(() => {
    if (!run || !lowStockSkus.length) {
      return null;
    }
    return {
      datasetName: "sales_order_master",
      transformId: "sales_order_master_date_pivot",
      folder: "outputs",
      runSettingsJson,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: {
        min_date: forecastMinDate,
        max_date: forecastMaxDate,
        date_field: "order_placed_week",
        value_field: "quantity",
        group_by_fields: ["sku", "location", "channel"],
        filters: [["sku", "in", lowStockSkus]] as unknown[],
      },
    };
  }, [run, runSettingsJson, lowStockSkus, forecastMinDate, forecastMaxDate]);
  const { table: channelForecastTable, loading: channelForecastLoading } =
    useMatrixTable(channelForecastVariables);
  const channelForecast = useMemo(
    () =>
      extractChannelForecast(channelForecastTable, {
        fromMs: bounds.forecastStartMs,
        toMs: bounds.forecastEndMs,
      }),
    [channelForecastTable, bounds]
  );

  // Inventory $ by location at the as-of week.
  const valueByLocation = useMemo(() => {
    const rows = extractWeeklyRows(invValueTable, "week", "location", "cost");
    if (!rows.length) {
      return null;
    }
    const pastWeeks = rows.filter((r) => r.weekMs <= asOfMs);
    const pool = pastWeeks.length ? pastWeeks : rows;
    const asOfWeekMs = Math.max(...pool.map((r) => r.weekMs));
    const map = new Map<string, number>();
    for (const row of pool) {
      if (row.weekMs === asOfWeekMs) {
        map.set(row.seg, (map.get(row.seg) ?? 0) + row.value);
      }
    }
    return map;
  }, [invValueTable, asOfMs]);

  // Open POs: units inbound per location, next arrival, and total value.
  const inboundFigures = useMemo(() => {
    const qtyRows = extractWeeklyRows(
      poQtyTable,
      "eta_to_warehouse_week",
      "location",
      "quantity"
    );
    const costRows = extractWeeklyRows(
      poCostTable,
      "eta_to_warehouse_week",
      "TOTAL",
      "cost"
    );
    if (!qtyRows.length && !costRows.length) {
      return {
        unitsByLocation: null as Map<string, number> | null,
        nextArrivalByLocation: new Map<string, string>(),
        totalUnits: null as number | null,
        totalValue: null as number | null,
        nextArrival: null as string | null,
      };
    }
    const nowMs = asOfMs;
    const inPoWindow = (ms: number) =>
      ms >= bounds.poStartMs && ms <= bounds.poEndMs;
    const unitsByLocation = new Map<string, number>();
    const nextMsByLocation = new Map<string, number>();
    let totalUnits = 0;
    for (const row of qtyRows.filter((r) => inPoWindow(r.weekMs))) {
      totalUnits += row.value;
      unitsByLocation.set(
        row.seg,
        (unitsByLocation.get(row.seg) ?? 0) + row.value
      );
      if (row.weekMs >= nowMs) {
        const prev = nextMsByLocation.get(row.seg);
        if (prev == null || row.weekMs < prev) {
          nextMsByLocation.set(row.seg, row.weekMs);
        }
      }
    }
    const nextArrivalByLocation = new Map<string, string>();
    let earliestMs: number | null = null;
    for (const [loc, ms] of nextMsByLocation) {
      nextArrivalByLocation.set(loc, dayjs.utc(ms).format("MM/DD"));
      if (earliestMs == null || ms < earliestMs) {
        earliestMs = ms;
      }
    }
    const boundedCost = costRows.filter((r) => inPoWindow(r.weekMs));
    const totalValue = boundedCost.length
      ? boundedCost.reduce((a, r) => a + r.value, 0)
      : null;
    return {
      unitsByLocation: qtyRows.length ? unitsByLocation : null,
      nextArrivalByLocation,
      totalUnits: qtyRows.length ? totalUnits : null,
      totalValue,
      nextArrival: earliestMs == null ? null : dayjs.utc(earliestMs).format("MM/DD"),
    };
  }, [poQtyTable, poCostTable, asOfMs, bounds]);

  // ── Assemble ──────────────────────────────────────────────────
  const atRiskByLocation = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const note = (location: string, sku: string) => {
      const set = map.get(location) ?? new Set<string>();
      set.add(sku);
      map.set(location, set);
    };
    // Same source preference as the risk tables (walk-computed current
    // values first, alerts only when the walk never loaded), so the map's
    // at-risk markers name the same SKUs the tables show.
    if (skuState.size > 0) {
      computedRisks.stockOuts.forEach((r) => note(r.location, r.sku));
      computedRisks.lowStock.forEach((r) => note(r.location, r.sku));
    } else {
      (alerts?.stock_outs.items ?? []).forEach((a) => note(a.location, a.sku));
      (alerts?.low_stock.items ?? []).forEach((a) => note(a.location, a.sku));
    }
    return map;
  }, [alerts, computedRisks, skuState]);

  const { locations, inTransitUnits, emptyLocationsHidden } = useMemo(() => {
    const segFields = isHubAndSpoke
      ? ["location", "location_role"]
      : ["location"];
    const rows = extractAsOfRows(locationTable, segFields, asOfMs);

    if (!rows.length) {
      // Fallback: alert lists are the only per-location signal — names and
      // at-risk counts only, every other figure null (em dash).
      const fallback = [...atRiskByLocation.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
          ([location, skus]): SnapshotLocation => ({
            id: location,
            name: location,
            short: location,
            role: isHubAndSpoke ? "spoke" : "dc",
            units: null,
            value: null,
            wos: null,
            targetWos: null,
            inStock: null,
            skus: null,
            atRisk: skus.size,
            weeklyOut: null,
            weeklyOutPrev: null,
            channelMix: null,
            displayWos: null,
            displayTargetWos: null,
            inTransit: null,
            inbound: null,
            nextArrival: null,
          })
        );
      // The alert-list fallback has no matrix to judge emptiness from.
      return { locations: fallback, inTransitUnits: null, emptyLocationsHidden: 0 };
    }

    type Acc = {
      role: "hub" | "spoke" | "dc";
      units: number | null;
      wos: number | null;
      targetWos: number | null;
      inStock: number | null;
      /** Which role supplied wos/target — hub rows must win over the same
       *  location's dual-role spoke rows. */
      coverPriority: number;
    };
    const rolePriority = (role: string): number =>
      role === "hub" ? 3 : role === "hub_spoke" ? 2 : 1;
    const byLocation = new Map<string, Acc>();
    let transitUnits: number | null = null;

    for (const row of rows) {
      const location = row.segs.location;
      const role = row.segs.location_role ?? "";
      if (isHubAndSpoke && (role === "in_transit" || location === IN_TRANSIT_LOCATION)) {
        if (row.measure === "inventory" && row.value != null) {
          transitUnits = (transitUnits ?? 0) + row.value;
        }
        continue;
      }
      const acc = byLocation.get(location) ?? {
        role: isHubAndSpoke
          ? role === "hub" || role === "hub_spoke"
            ? ("hub" as const)
            : ("spoke" as const)
          : ("dc" as const),
        units: null,
        wos: null,
        targetWos: null,
        inStock: null,
        coverPriority: 0,
      };
      // A dual-role location is a hub that also serves as its own spoke.
      if (isHubAndSpoke && (role === "hub" || role === "hub_spoke")) {
        acc.role = "hub";
      }
      const priority = isHubAndSpoke ? rolePriority(role) : 1;
      if (row.value != null) {
        // Cora: "inventory" / "weeks of supply" / "target weeks of supply".
        // Flat: "ending inventory on hand" / "ending weeks of supply" /
        //       "target weeks of supply" / "in stock rate" (0..1).
        if (row.measure === "inventory" || row.measure === "ending inventory on hand") {
          acc.units = (acc.units ?? 0) + row.value;
        } else if (
          row.measure === "weeks of supply" ||
          row.measure === "ending weeks of supply"
        ) {
          if (priority >= acc.coverPriority) {
            acc.wos = row.value;
            acc.coverPriority = priority;
          }
        } else if (row.measure === "target weeks of supply") {
          if (priority >= acc.coverPriority) {
            acc.targetWos = row.value;
            acc.coverPriority = priority;
          }
        } else if (row.measure === "in stock rate") {
          acc.inStock = row.value * 100;
        }
      }
      byLocation.set(location, acc);
    }

    const assembled = [...byLocation.entries()]
      .map(
        ([location, acc]): SnapshotLocation => ({
          id: location,
          name: location,
          short: location,
          role: acc.role,
          units: acc.units,
          value: null,
          wos: acc.wos,
          targetWos: acc.targetWos,
          inStock: acc.inStock,
          skus: null,
          atRisk: atRiskByLocation.get(location)?.size ?? 0,
          weeklyOut: null,
          weeklyOutPrev: null,
          channelMix: null,
          displayWos: acc.wos,
          displayTargetWos: acc.targetWos,
          inTransit: null,
          inbound: null,
          nextArrival: null,
        })
      )
      .sort((a, b) => {
        if (a.role === "hub" && b.role !== "hub") {
          return -1;
        }
        if (b.role === "hub" && a.role !== "hub") {
          return 1;
        }
        return (b.units ?? 0) - (a.units ?? 0);
      });

    // Drop locations the plan has nothing to say about: no stock on hand and
    // no forecast demand ahead of the data boundary. They occupy a full row
    // and a full node while carrying no decision.
    //
    // Forward demand is summed from the as-of period onward, not read at the
    // as-of period alone — a location can be idle this week and still have
    // demand next month, and that is not an empty location.
    const { sums: forwardDemand } = extractHorizonSums(
      locationTable,
      ["location"],
      "forecasted sales",
      { fromMs: asOfMs, toMs: Number.MAX_SAFE_INTEGER }
    );
    const isEmpty = (l: SnapshotLocation): boolean =>
      // null is NOT zero. A null figure means the measure is missing for this
      // location, which is a data gap the coverage check exists to surface —
      // hiding it here would bury the very thing worth seeing.
      l.units === 0 &&
      Math.abs(forwardDemand.get(l.id) ?? 0) === 0 &&
      // Anything the plan has flagged is not empty, whatever its stock reads.
      // Otherwise the risk tables would name a location the map never shows.
      (l.atRisk ?? 0) === 0;

    const kept = assembled.filter((l) => !isEmpty(l));
    // Never empty the page. If every location looks empty the problem is the
    // data, not the network, and a blank view hides that.
    const visible = kept.length ? kept : assembled;
    return {
      locations: visible,
      inTransitUnits: transitUnits,
      emptyLocationsHidden: assembled.length - visible.length,
    };
  }, [locationTable, isHubAndSpoke, atRiskByLocation, asOfMs]);

  const hubStockBySku = useMemo(() => {
    if (!isHubAndSpoke || !hubCoverTable) {
      return null;
    }
    const rows = extractAsOfRows(hubCoverTable, ["sku", "location_role"], asOfMs);
    if (!rows.length) {
      return null;
    }
    const map = new Map<string, number>();
    for (const row of rows) {
      const role = row.segs.location_role;
      if (
        row.measure === "inventory" &&
        (role === "hub" || role === "hub_spoke") &&
        row.value != null
      ) {
        map.set(row.segs.sku, (map.get(row.segs.sku) ?? 0) + row.value);
      }
    }
    return map;
  }, [isHubAndSpoke, hubCoverTable, asOfMs]);

  // Per-lane hub→spoke flow from the spoke walk's open transfer arrivals
  // (the transfer matrix force-adds source_hub to the segmentation). Units
  // are the open transfers arriving over the fetched horizon; ETA is the
  // first future week with an arrival.
  const transferLanes = useMemo(() => {
    if (!rebalanceTable) {
      return null;
    }
    const periodCols = rebalanceTable.schema.fields
      .map((f) => f.name)
      .filter((n) => /^\d+$/.test(n))
      .filter(
        (n) =>
          Number(n) >= bounds.walkStartMs && Number(n) <= bounds.rebalanceEndMs
      )
      .sort((a, b) => Number(a) - Number(b));
    const measureVec = rebalanceTable.getChild("measure");
    const hubVec = rebalanceTable.getChild("source_hub");
    const locVec = rebalanceTable.getChild("location");
    const valueVecs = periodCols.map((c) => rebalanceTable.getChild(c));
    const nowMs = asOfMs;
    const lanes = new Map<
      string,
      { fromHubId: string; toLocationId: string; units: number; etaMs: number | null }
    >();
    for (let i = 0; i < rebalanceTable.numRows; i++) {
      if (String(measureVec?.get(i) ?? "").toLowerCase() !== "open") {
        continue;
      }
      const fromHubId = String(hubVec?.get(i) ?? "");
      const toLocationId = String(locVec?.get(i) ?? "");
      const key = fromHubId + "|" + toLocationId;
      const lane = lanes.get(key) ?? {
        fromHubId,
        toLocationId,
        units: 0,
        etaMs: null,
      };
      for (let c = 0; c < periodCols.length; c++) {
        const value = toNumber(valueVecs[c]?.get(i)) ?? 0;
        if (value > 0) {
          lane.units += value;
          const ms = Number(periodCols[c]);
          if (ms >= nowMs && (lane.etaMs == null || ms < lane.etaMs)) {
            lane.etaMs = ms;
          }
        }
      }
      lanes.set(key, lane);
    }
    return [...lanes.values()]
      .filter((lane) => lane.units > 0)
      .map((lane) => ({
        fromHubId: lane.fromHubId,
        toLocationId: lane.toLocationId,
        units: Math.round(lane.units),
        eta: lane.etaMs == null ? null : dayjs.utc(lane.etaMs).format("MM/DD"),
      }));
  }, [rebalanceTable, asOfMs, bounds]);

  // Actual shipments. Trailing totals always cover the 13-week window; the
  // per-period flow figures come from whichever weeks the period selects —
  // week mode averages the last 4 complete weeks, month mode totals every
  // complete week of the as-of month so far (never a weekly figure scaled up).
  const salesFigures = useMemo(() => {
    const weeklyQty = extractFlowRows(
      salesQtyTable,
      "order_placed_week",
      "quantity"
    );
    const weeklyRev = extractChannelValueRows(
      salesRevTable,
      "order_placed_week",
      "revenue"
    );

    // Trailing strip: the 13 complete weeks before the run's week.
    const inTrailing = (ms: number) =>
      ms >= bounds.trailingStartMs && ms < bounds.weekStartMs;
    const trailingQty = weeklyQty.filter((r) => inTrailing(r.periodMs));
    const trailingRev = weeklyRev.filter((r) => inTrailing(r.periodMs));
    const trailingUnits = trailingQty.length
      ? trailingQty.reduce((a, r) => a + r.value, 0)
      : null;
    const trailingRevenue = trailingRev.length
      ? trailingRev.reduce((a, r) => a + r.value, 0)
      : null;

    // Flow source + how many periods it spans, so sums become a rate.
    const inRecentWeeks = (ms: number) =>
      ms >= recentCutoffMs && ms < bounds.weekStartMs;
    // Half-open: the week containing the data boundary is excluded, because
    // sales_order_master fills the in-progress week with forecast.
    const inMonthToDate = (ms: number) =>
      ms >= bounds.mtdStartMs && ms < bounds.mtdEndMs;
    const flowQty = isMonth
      ? weeklyQty.filter((r) => inMonthToDate(r.periodMs))
      : weeklyQty.filter((r) => inRecentWeeks(r.periodMs));
    const flowRev = isMonth
      ? weeklyRev.filter((r) => inMonthToDate(r.periodMs))
      : weeklyRev.filter((r) => inRecentWeeks(r.periodMs));
    // Month mode is a total over the elapsed weeks; week mode is a rate.
    const divisor = isMonth ? 1 : RECENT_WEEKS;

    if (!flowQty.length) {
      return {
        outflowByLocation: null as Map<string, number> | null,
        mixByLocation: null as Map<string, Map<string, number>> | null,
        channelUnits: null as Map<string, number> | null,
        channelRevenue: null as Map<string, number> | null,
        periodUnits: null as number | null,
        periodRevenue: null as number | null,
        trailingUnits,
        trailingRevenue,
      };
    }

    // Every downstream figure is derived from these same rows, so
    // Σ(location outflow) === Σ(channel units) === the period total.
    const outflowByLocation = new Map<string, number>();
    const mixByLocation = new Map<string, Map<string, number>>();
    const channelUnits = new Map<string, number>();
    for (const row of flowQty) {
      const units = row.value / divisor;
      outflowByLocation.set(
        row.location,
        (outflowByLocation.get(row.location) ?? 0) + units
      );
      const mix = mixByLocation.get(row.location) ?? new Map<string, number>();
      mix.set(row.channel, (mix.get(row.channel) ?? 0) + units);
      mixByLocation.set(row.location, mix);
      channelUnits.set(
        row.channel,
        (channelUnits.get(row.channel) ?? 0) + units
      );
    }
    const channelRevenue = new Map<string, number>();
    for (const row of flowRev) {
      channelRevenue.set(
        row.channel,
        (channelRevenue.get(row.channel) ?? 0) + row.value / divisor
      );
    }
    return {
      outflowByLocation,
      mixByLocation,
      channelUnits,
      channelRevenue: flowRev.length ? channelRevenue : null,
      periodUnits: [...outflowByLocation.values()].reduce((a, b) => a + b, 0),
      periodRevenue: flowRev.length
        ? [...channelRevenue.values()].reduce((a, b) => a + b, 0)
        : null,
      trailingUnits,
      trailingRevenue,
    };
  }, [salesQtyTable, salesRevTable, isMonth, recentCutoffMs, bounds]);

  // Top channels by units for the period; everything else rolls into one
  // neutral remainder. Real customers carry 20-30+ channels, so a fixed
  // per-channel encoding is not viable — the roll-up is what keeps the
  // legend, the node list and the mix bars readable and consistent.
  const channels = useMemo<SnapshotChannel[]>(() => {
    const units = salesFigures.channelUnits;
    const total = salesFigures.periodUnits;
    if (!units || !total) {
      return [];
    }
    const ranked = [...units.entries()].sort((a, b) => b[1] - a[1]);
    // A lone placeholder channel is an absent dimension, not a split —
    // drawing one full-width "Unspecified" bar would imply information
    // the data does not carry.
    if (ranked.length <= 1 && ranked[0]?.[0] === UNSPECIFIED_CHANNEL) {
      return [];
    }
    const top = ranked.slice(0, TOP_CHANNELS);
    const rest = ranked.slice(TOP_CHANNELS);
    const rows: SnapshotChannel[] = top.map(([channel, u]) => ({
      channel,
      units: u,
      revenue: salesFigures.channelRevenue?.get(channel) ?? null,
      share: total > 0 ? u / total : 0,
      isOther: false,
    }));
    if (rest.length) {
      const restUnits = rest.reduce((a, [, u]) => a + u, 0);
      const restRevenue = salesFigures.channelRevenue
        ? rest.reduce(
            (a, [c]) => a + (salesFigures.channelRevenue?.get(c) ?? 0),
            0
          )
        : null;
      rows.push({
        channel: OTHER_CHANNEL_LABEL,
        units: restUnits,
        revenue: restRevenue,
        share: total > 0 ? restUnits / total : 0,
        isOther: true,
      });
    }
    return rows;
  }, [salesFigures]);

  /** Buckets a location's own channels into the same set the legend shows. */
  const mixForLocation = useMemo(() => {
    const names = new Set(
      channels.filter((c) => !c.isOther).map((c) => c.channel)
    );
    return (location: string): SnapshotChannelMixEntry[] | null => {
      const mix = salesFigures.mixByLocation?.get(location);
      if (!mix) {
        return null;
      }
      const bucketed = new Map<string, number>();
      for (const [channel, units] of mix) {
        const key = names.has(channel) ? channel : OTHER_CHANNEL_LABEL;
        bucketed.set(key, (bucketed.get(key) ?? 0) + units);
      }
      const total = [...bucketed.values()].reduce((a, b) => a + b, 0);
      if (total <= 0) {
        return null;
      }
      // Order matches the legend so colors read consistently across rows.
      const order = channels.map((c) => c.channel);
      return [...bucketed.entries()]
        .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
        .map(([channel, units]) => ({
          channel,
          units: Math.round(units),
          share: units / total,
        }));
    };
  }, [channels, salesFigures]);

  // Per-location in-stock from the SKU-level rows, for networks whose
  // location matrix has no in-stock measure (the Cora Inventory view).
  const inStockByLocation = useMemo(() => {
    const acc = new Map<string, { weighted: number; weight: number }>();
    for (const s of skuState.values()) {
      if (s.inStock == null) {
        continue;
      }
      const impliedWeekly =
        s.onHand != null && s.onHand > 0 && s.wos != null && s.wos > 0
          ? s.onHand / s.wos
          : null;
      const weight = impliedWeekly ?? s.weeklyDemand ?? 0;
      if (weight <= 0) {
        continue;
      }
      const entry = acc.get(s.location) ?? { weighted: 0, weight: 0 };
      entry.weighted += s.inStock * weight;
      entry.weight += weight;
      acc.set(s.location, entry);
    }
    const map = new Map<string, number>();
    for (const [location, entry] of acc) {
      if (entry.weight > 0) {
        map.set(location, (entry.weighted / entry.weight) * 100);
      }
    }
    return map;
  }, [skuState]);

  /** Months-of-supply values for month mode, read from the run's own
   *  monthly matrix. Absent → the figure renders as an em dash rather than
   *  a weekly number multiplied by 4.35. */
  const monthSupplyByLocation = useMemo(() => {
    if (!isMonth || !monthSupplyTable) {
      return null;
    }
    const segFields = isHubAndSpoke
      ? ["location", "location_role"]
      : ["location"];
    const rows = extractAsOfRows(monthSupplyTable, segFields, asOfMs);
    if (!rows.length) {
      return null;
    }
    const map = new Map<string, { mos: number | null; target: number | null }>();
    for (const row of rows) {
      const role = row.segs.location_role ?? "";
      if (isHubAndSpoke && (role === "in_transit" || !row.segs.location)) {
        continue;
      }
      const priority = isHubAndSpoke
        ? role === "hub"
          ? 3
          : role === "hub_spoke"
          ? 2
          : 1
        : 1;
      const entry = map.get(row.segs.location) ?? { mos: null, target: null };
      // Hub-role rows win, mirroring the weekly matrix's precedence.
      if (row.value != null && priority >= 1) {
        if (row.measure === "months of supply") {
          entry.mos = row.value;
        } else if (row.measure === "target months of supply") {
          entry.target = row.value;
        } else if (row.measure === "ending months of supply") {
          entry.mos = row.value;
        }
      }
      map.set(row.segs.location, entry);
    }
    return map;
  }, [isMonth, monthSupplyTable, isHubAndSpoke, asOfMs]);

  const locationsWithSales = useMemo<SnapshotLocation[]>(() => {
    const outflowByLoc = salesFigures.outflowByLocation;
    return locations.map((l) => {
      const monthSupply = monthSupplyByLocation?.get(l.id);
      return {
        ...l,
        weeklyOut: outflowByLoc ? Math.round(outflowByLoc.get(l.id) ?? 0) : null,
        channelMix: mixForLocation(l.id),
        value: valueByLocation?.get(l.id) ?? null,
        inStock: l.inStock ?? inStockByLocation.get(l.id) ?? null,
        inbound: inboundFigures.unitsByLocation
          ? Math.round(inboundFigures.unitsByLocation.get(l.id) ?? 0)
          : null,
        nextArrival: inboundFigures.nextArrivalByLocation.get(l.id) ?? null,
        // wos/targetWos stay weekly — health color is computed from that
        // one ratio in both modes so colors never shift with the toggle.
        displayWos: isMonth ? monthSupply?.mos ?? null : l.wos,
        displayTargetWos: isMonth ? monthSupply?.target ?? null : l.targetWos,
      };
    });
  }, [
    locations,
    salesFigures,
    mixForLocation,
    valueByLocation,
    inStockByLocation,
    inboundFigures,
    monthSupplyByLocation,
    isMonth,
  ]);

  // Turn rate is DERIVED from the rows the page draws — Σ outflow ÷ Σ stock
  // over the same locations — so it can never contradict the per-location
  // figures sitting directly above it.
  const sales = useMemo<SnapshotSales>(() => {
    const rows = locationsWithSales.filter(
      (l) => l.units != null && l.weeklyOut != null
    );
    const stock = rows.reduce((a, l) => a + (l.units ?? 0), 0);
    const outflow = rows.reduce((a, l) => a + (l.weeklyOut ?? 0), 0);
    // Week mode's figures are already per-week rates; month mode's are totals
    // spanning however many complete weeks the month has produced so far.
    const figureWeeks = isMonth ? monthWeeks : 1;
    const toWeeklyRate = (total: number | null) =>
      total == null || figureWeeks <= 0 ? null : total / figureWeeks;
    const outflowRate = toWeeklyRate(outflow);
    return {
      weeklyUnits:
        salesFigures.periodUnits == null
          ? null
          : Math.round(salesFigures.periodUnits),
      weeklyUnitsPrev: null,
      weeklyRevenue: salesFigures.periodRevenue,
      weeklyRevenuePrev: null,
      weeklyRateUnits: toWeeklyRate(salesFigures.periodUnits),
      // Expressed in the selected period's unit, but always built from the
      // weekly rate, so a month-to-date turn is a full month's turn rather
      // than however much of the month happens to have elapsed.
      turnRate:
        rows.length && stock > 0 && outflowRate != null
          ? (outflowRate * (isMonth ? WEEKS_PER_MONTH : 1)) / stock
          : null,
      turnRatePrev: null,
    };
  }, [locationsWithSales, salesFigures, isMonth, monthWeeks]);

  const rebalance = useMemo<SnapshotRebalanceCard[]>(() => {
    if (!isHubAndSpoke || !rebalanceTable) {
      return [];
    }
    const { sums } = extractHorizonSums(
      rebalanceTable,
      ["location"],
      "transfer order to place",
      { fromMs: bounds.walkStartMs, toMs: bounds.rebalanceEndMs }
    );
    // These are hub→spoke moves: a hub-role location showing up as a
    // destination is its own dual-role row, not a rebalance target.
    const hubIds = new Set(
      locations.filter((l) => l.role === "hub").map((l) => l.id)
    );
    return [...sums.entries()]
      .filter(([location, units]) => units > 0 && !hubIds.has(location))
      .sort(([, a], [, b]) => b - a)
      .map(([location, units]) => ({
        toLocationId: location,
        toLocationName: location,
        units: Math.round(units),
        skus: null,
        value: null,
      }));
  }, [isHubAndSpoke, rebalanceTable, locations]);

  return {
    locations: locationsWithSales,
    inbound: {
      units: inboundFigures.totalUnits,
      value: inboundFigures.totalValue,
      shipments: null,
      nextArrival: inboundFigures.nextArrival,
    },
    sales,
    rebalance,
    inTransitUnits,
    transferLanes,
    hubStockBySku,
    trailingActuals: {
      units: salesFigures.trailingUnits,
      revenue: salesFigures.trailingRevenue,
    },
    networkInStock,
    openPoValue: inboundFigures.totalValue,
    channelForecast,
    channels,
    emptyLocationsHidden,
    monthLabel,
    monthWeeks,
    computedRisks,
    computedRisksReady: skuState.size > 0,
    loading:
      locationsLoading ||
      hubCoverLoading ||
      rebalanceLoading ||
      salesQtyLoading ||
      salesRevLoading ||
      monthSupplyLoading ||
      invValueLoading ||
      invSkuValueLoading ||
      poQtyLoading ||
      poCostLoading ||
      riskPrimaryLoading ||
      riskSpokeLoading ||
      channelForecastLoading,
  };
};
